import type { DocumentPosition, WorkspaceOperation } from '@/service/api'

/**
 * Given a set of document IDs whose shapes were just deleted from the canvas
 * and the current `selectedDocuments`, compute what to remove from the board.
 *
 * Returns `null` when none of the deleted IDs are still selected (e.g. the doc
 * was already removed, or the deleted shape never corresponded to a board doc)
 * so the caller can skip the removeSelectedDocuments + saveBoard round-trip.
 *
 * Pure on purpose: the trigger (which tldraw event fires this) lives in
 * BoardCanvas, but the "which docs actually go" decision is testable here.
 */
export function computeDocumentRemoval(
  deletedDocumentIds: Iterable<string>,
  selectedDocuments: DocumentPosition[],
): { removedIds: string[]; remaining: DocumentPosition[]; operations: WorkspaceOperation } | null {
  const deleteSet = new Set(deletedDocumentIds)
  if (deleteSet.size === 0) return null

  const removedIds = selectedDocuments
    .filter((doc) => deleteSet.has(doc.document_id))
    .map((doc) => doc.document_id)
  if (removedIds.length === 0) return null

  const removedSet = new Set(removedIds)
  const remaining = selectedDocuments.filter((doc) => !removedSet.has(doc.document_id))

  return {
    removedIds,
    remaining,
    operations: { upsert: remaining, remove: removedIds },
  }
}
